import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import { useCurrenciesSlice } from '../slice';
import { Form } from 'app/components/ui/form';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ScrollArea } from 'app/components/ui/scroll-area';
import { ChevronDown, Search, X } from 'lucide-react';
import { useToast } from 'app/components/ui/use-toast';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddCurrencyModal({ open, onOpenChange }: Props) {
  const { useAddCurrencyMutation, useGetAllCurrencyQuery } =
    useCurrenciesSlice();
  const [addCurrency, { data, error, isLoading }] = useAddCurrencyMutation();
  const [getCurrency, { data: currencies }] = useGetAllCurrencyQuery();
  const form = useForm();
  const { toast } = useToast();

  // Custom dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<any>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      getCurrency({});
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleAdd = () => {
    const formData = form.getValues();

    // Validate required fields
    if (!formData.currency_code || !formData.own_rate) {
      toast({
        title: 'Please select a currency and enter your own rate',
        variant: 'destructive',
      });
      return;
    }

    const status = formData.status ? 1 : 0;
    addCurrency({
      ...formData,
      rate: formData.own_rate, // Use own_rate as the rate
      status,
    });
  };

  useEffect(() => {
    if (data) {
      toast({
        title: data.message,
        variant: 'success',
      });
      form.reset();
      onOpenChange(false);
    }
    if (error) {
      const errorMessage =
        (error as any)?.data?.message || 'Failed to add currency';
      toast({
        title: errorMessage,
        variant: 'destructive',
      });
    }
  }, [data, error, form, onOpenChange]);

  const handleCurrencyChange = (currency: any) => {
    setSelectedCurrency(currency);
    form.setValue('currency_symbol', currency.currency_symbol || '');
    form.setValue('currency_code', currency.currency_code || '');
    form.setValue('own_rate', ''); // Clear own rate when currency changes
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handleClose = () => {
    form.reset();
    setSelectedCurrency(null);
    setSearchTerm('');
    setIsDropdownOpen(false);
    onOpenChange(false);
  };

  // Filter currencies based on search term
  const filteredCurrencies =
    currencies?.data?.filter((currency: any) => {
      const countryName = currency.country_name || '';
      const currencyCode = currency.currency_code || '';
      const currencySymbol = currency.currency_symbol || '';

      return (
        countryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currencyCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currencySymbol.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }) || [];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Currency</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleAdd)} className="p-6">
            <div className="space-y-6">
              <div>
                <Label htmlFor="currency" className="mb-2">
                  Currency <span className="text-red-500">*</span>
                </Label>
                <div className="relative" ref={dropdownRef}>
                  <div
                    className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-white cursor-pointer hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 text-sm"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span
                      className={
                        selectedCurrency ? 'text-gray-900' : 'text-gray-500'
                      }
                    >
                      {selectedCurrency
                        ? `${selectedCurrency.currency_code || 'N/A'}`
                        : 'Select currency'}
                    </span>
                    <div className="flex items-center space-x-2">
                      {selectedCurrency && (
                        <X
                          className="w-4 h-4 text-gray-400 hover:text-gray-600"
                          onClick={e => {
                            e.stopPropagation();
                            setSelectedCurrency(null);
                            form.setValue('currency_symbol', '');
                            form.setValue('currency_code', '');
                            form.setValue('own_rate', '');
                          }}
                        />
                      )}
                      <ChevronDown
                        className={`w-4 h-4 text-gray-400 transition-transform ${
                          isDropdownOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </div>
                  </div>

                  {isDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search currencies..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="w-full pl-8 pr-3 py-2 text-xs border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            onClick={e => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredCurrencies.length > 0 ? (
                          filteredCurrencies.map(
                            (currency: any, index: number) => (
                              <div
                                key={index}
                                className="px-3 py-2 text-xs cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                                onClick={() => handleCurrencyChange(currency)}
                              >
                                <div className="text-gray-600">
                                  {currency.currency_code || 'N/A'}
                                </div>
                              </div>
                            ),
                          )
                        ) : (
                          <div className="px-3 py-4 text-xs text-gray-500 text-center">
                            No currencies found
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="currency_symbol" className="mb-2">
                  Currency Symbol
                </Label>
                <Input
                  id="currency_symbol"
                  {...form.register('currency_symbol')}
                  placeholder="e.g., $, €, ₹"
                />
              </div>

              <div>
                <Label htmlFor="rate" className="mb-2">
                  Live Rate <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="live_rate"
                  type="number"
                  value={selectedCurrency?.live_rate || ''}
                  disabled
                  placeholder="Live rate from API"
                />
              </div>

              <div>
                <Label htmlFor="own_rate" className="mb-2">
                  Own Rate <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="own_rate"
                  type="number"
                  step="0.000001"
                  {...form.register('own_rate', {
                    required: 'Own rate is required',
                    min: { value: 0, message: 'Rate must be positive' },
                  })}
                  placeholder="1.000000"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="status"
                  {...form.register('status')}
                  defaultChecked={true}
                />
                <Label
                  htmlFor="status"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Active
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Adding...' : 'Add Currency'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
